import { FC, useEffect, useState } from 'react'
import { Plus, Trash2, Mail, MessageCircle, Webhook } from 'lucide-react'
import { TOKENS, ConfirmDialog } from '../../../components/design-system'
import {
  TriggerType, TRIGGER_LABELS,
  ActionChannel, CHANNEL_LABELS,
  RoleSelector, ROLE_LABELS,
  AlarmTypeKey, ALARM_TYPE_LABELS, ALARM_TYPE_VALUES,
  NotificationRule, NotificationRuleTriggerDTO, NotificationRuleActionDTO,
  RepeatStrategy, REPEAT_STRATEGY_LABELS, REPEAT_STRATEGY_DESCRIPTIONS,
  REPEAT_SUPPORTED_TRIGGERS, ESCALATION_PRESETS,
} from '../types'

type Props = {
  initial: NotificationRule | null
  defaultScope: 'global' | 'company'
  defaultCompanyId?: number
  onSave: (body: any) => Promise<void>
  onCancel: () => void
  saving?: boolean
  // Premium tier flag (from /me/permissions). Locks the reminder
  // section to 'none' when false. Backend re-enforces — the UI lock
  // is only a UX cue.
  premiumEnabled?: boolean
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
  textTransform: 'uppercase', color: TOKENS.color.muted, display: 'block', marginBottom: 4,
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${TOKENS.color.border}`,
  borderRadius: TOKENS.radius.chip,
  background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
}

const sectionCard: React.CSSProperties = {
  background: TOKENS.color.surface,
  border: `1px solid ${TOKENS.color.border}`,
  borderRadius: TOKENS.radius.card,
  padding: TOKENS.spacing.md,
  marginBottom: TOKENS.spacing.sm,
}

const channelIcon: Record<ActionChannel, JSX.Element> = {
  email: <Mail size={14} />,
  whatsapp: <MessageCircle size={14} />,
  webhook: <Webhook size={14} />,
}

function emptyTriggerForType(type: TriggerType): NotificationRuleTriggerDTO {
  const defaults: Record<TriggerType, Record<string, unknown>> = {
    alarm_active:        {},
    alarm_recurrent:     { count: 5, window_days: 7 },
    box_offline:         { minutes: 30 },
    alarm_unattended:    { minutes: 15 },
    alarm_burst:         { count: 3, window_minutes: 5 },
    box_silent:          { days: 7 },
    command_failed:      {},
    group_alarm:         { count: 3, window_minutes: 10 },
    energy_outage:       { count: 3, window_minutes: 5 },
    token_revoked:       {},
  }
  return { trigger_type: type, params: defaults[type] }
}

function emptyActionForChannel(channel: ActionChannel): NotificationRuleActionDTO {
  return {
    channel,
    recipients: channel === 'webhook' ? { url: '' } : { roles: ['admin'] },
    template: null,
    params: {},
  }
}

const TriggerParamsEditor: FC<{ trigger: NotificationRuleTriggerDTO; onChange: (t: NotificationRuleTriggerDTO) => void }> = ({ trigger, onChange }) => {
  const set = (params: Record<string, unknown>) => onChange({ ...trigger, params: { ...trigger.params, ...params } })

  const numField = (key: string, label: string) => (
    <div style={{ flex: '1 1 100px' }}>
      <label style={labelStyle}>{label}</label>
      <input
        type='number'
        min={1}
        style={inputStyle}
        value={Number(trigger.params[key] ?? 0)}
        onChange={(e) => set({ [key]: Number(e.target.value) })}
      />
    </div>
  )

  const alarmTypesField = () => {
    const current = (trigger.params.alarm_types as AlarmTypeKey[] | undefined) ?? []
    const toggle = (k: AlarmTypeKey) => {
      const next = current.includes(k) ? current.filter((x) => x !== k) : [...current, k]
      set({ alarm_types: next.length === 0 ? undefined : next })
    }
    return (
      <div style={{ flex: '1 1 100%' }}>
        <label style={labelStyle}>Tipos de alarme (vazio = todos)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ALARM_TYPE_VALUES.map((k) => {
            const on = current.includes(k)
            return (
              <button
                key={k} type='button' onClick={() => toggle(k)}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '4px 8px',
                  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                  border: `1px solid ${on ? TOKENS.color.action : TOKENS.color.border}`,
                  background: on ? TOKENS.color.actionSoft : TOKENS.color.card,
                  color: on ? TOKENS.color.action : TOKENS.color.muted,
                }}
              >{ALARM_TYPE_LABELS[k]}</button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: TOKENS.spacing.sm, marginTop: TOKENS.spacing.sm }}>
      {trigger.trigger_type === 'alarm_active' && alarmTypesField()}
      {trigger.trigger_type === 'alarm_recurrent' && (
        <>{numField('count', 'Quantidade')} {numField('window_days', 'Janela (dias)')} {alarmTypesField()}</>
      )}
      {trigger.trigger_type === 'alarm_unattended' && (
        <>{numField('minutes', 'Minutos')} {alarmTypesField()}</>
      )}
      {trigger.trigger_type === 'alarm_burst' && (
        <>{numField('count', 'Quantidade')} {numField('window_minutes', 'Janela (min)')} {alarmTypesField()}</>
      )}
      {trigger.trigger_type === 'box_offline' && numField('minutes', 'Minutos')}
      {trigger.trigger_type === 'box_silent' && numField('days', 'Dias')}
      {trigger.trigger_type === 'group_alarm' && (<>{numField('count', 'Quantidade')} {numField('window_minutes', 'Janela (min)')}</>)}
      {trigger.trigger_type === 'energy_outage' && (<>{numField('count', 'Quantidade')} {numField('window_minutes', 'Janela (min)')}</>)}
      {(trigger.trigger_type === 'command_failed' || trigger.trigger_type === 'token_revoked') && (
        <div style={{ color: TOKENS.color.muted, fontSize: 12 }}>Sem parâmetros adicionais.</div>
      )}
    </div>
  )
}

/**
 * Premium-only editor for reminder cadence. Hidden entirely for
 * trigger types that don't make sense to reminder (alarm_active fires
 * point-in-time). Locked to 'none' with a tooltip when premium is off
 * — the form still works on the free tier, just without the controls.
 */
const RepeatStrategyEditor: FC<{
  trigger: NotificationRuleTriggerDTO
  premiumEnabled: boolean
  onChange: (t: NotificationRuleTriggerDTO) => void
}> = ({ trigger, premiumEnabled, onChange }) => {
  if (!REPEAT_SUPPORTED_TRIGGERS.includes(trigger.trigger_type)) return null

  const strategy: RepeatStrategy = trigger.repeat_strategy ?? 'none'
  const interval = trigger.repeat_interval_minutes ?? null
  const steps = trigger.escalation_steps_minutes ?? null

  const setStrategy = (s: RepeatStrategy) => {
    onChange({
      ...trigger,
      repeat_strategy: s,
      // Reset other params when switching to keep state clean.
      repeat_interval_minutes: s === 'fixed' ? (interval ?? 240) : null,
      escalation_steps_minutes: s === 'escalation' ? (steps ?? ESCALATION_PRESETS[1].steps) : null,
    })
  }

  return (
    <div style={{
      marginTop: TOKENS.spacing.md,
      padding: TOKENS.spacing.sm,
      background: premiumEnabled ? TOKENS.color.actionSoft : TOKENS.color.surface,
      border: `1px dashed ${premiumEnabled ? TOKENS.color.action : TOKENS.color.border}`,
      borderRadius: TOKENS.radius.chip,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ ...labelStyle, marginBottom: 0 }}>Repetir notificação</span>
        {!premiumEnabled && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: TOKENS.color.border, color: TOKENS.color.muted }}>
            PREMIUM
          </span>
        )}
      </div>
      {!premiumEnabled && (
        <div style={{ fontSize: 11, color: TOKENS.color.muted, marginBottom: TOKENS.spacing.sm, lineHeight: 1.4 }}>
          Configurar lembretes recorrentes requer o plano premium habilitado para esta empresa.
          Peça ao admin da plataforma. Configuração disponível: <strong>não repete (padrão)</strong>.
        </div>
      )}
      <select
        value={strategy}
        disabled={!premiumEnabled}
        onChange={(e) => setStrategy(e.target.value as RepeatStrategy)}
        style={{ ...inputStyle, opacity: premiumEnabled ? 1 : 0.6, cursor: premiumEnabled ? 'pointer' : 'not-allowed' }}
      >
        {(['none', 'fixed', 'escalation', 'daily'] as RepeatStrategy[]).map((s) => (
          <option key={s} value={s}>{REPEAT_STRATEGY_LABELS[s]}</option>
        ))}
      </select>
      <div style={{ fontSize: 11, color: TOKENS.color.muted, marginTop: 4, lineHeight: 1.4 }}>
        {REPEAT_STRATEGY_DESCRIPTIONS[strategy]}
      </div>

      {strategy === 'fixed' && premiumEnabled && (
        <div style={{ marginTop: TOKENS.spacing.sm }}>
          <label style={labelStyle}>Intervalo (em minutos)</label>
          <input
            type='number'
            min={1}
            value={interval ?? 240}
            onChange={(e) => onChange({ ...trigger, repeat_interval_minutes: Math.max(1, Number(e.target.value) || 1) })}
            style={inputStyle}
          />
          <div style={{ fontSize: 10, color: TOKENS.color.muted, marginTop: 2 }}>
            Sugestões: 60 (1h), 240 (4h), 720 (12h), 1440 (24h)
          </div>
        </div>
      )}

      {strategy === 'escalation' && premiumEnabled && (
        <div style={{ marginTop: TOKENS.spacing.sm }}>
          <label style={labelStyle}>Passos (minutos a partir do primeiro disparo)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
            {ESCALATION_PRESETS.map((p) => (
              <button
                key={p.label} type='button'
                onClick={() => onChange({ ...trigger, escalation_steps_minutes: p.steps })}
                style={{
                  fontSize: 10, fontWeight: 600, padding: '4px 8px',
                  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                  border: `1px solid ${TOKENS.color.border}`,
                  background: TOKENS.color.card, color: TOKENS.color.muted,
                }}
              >{p.label}</button>
            ))}
          </div>
          <input
            type='text'
            value={(steps ?? []).join(', ')}
            onChange={(e) => {
              const arr = e.target.value
                .split(',')
                .map((s) => Number(s.trim()))
                .filter((n) => Number.isFinite(n) && n > 0)
              onChange({ ...trigger, escalation_steps_minutes: arr.length ? arr : null })
            }}
            placeholder='30, 120, 480, 1440'
            style={inputStyle}
          />
          <div style={{ fontSize: 10, color: TOKENS.color.muted, marginTop: 2 }}>
            Lista de minutos separados por vírgula. Após o último passo, para.
          </div>
        </div>
      )}
    </div>
  )
}

const RecipientsEditor: FC<{ action: NotificationRuleActionDTO; onChange: (a: NotificationRuleActionDTO) => void }> = ({ action, onChange }) => {
  const setRecipients = (rec: any) => onChange({ ...action, recipients: { ...action.recipients, ...rec } })
  if (action.channel === 'webhook') {
    return (
      <div>
        <label style={labelStyle}>URL do webhook</label>
        <input
          type='url'
          style={inputStyle}
          placeholder='https://...'
          value={(action.recipients as any).url ?? ''}
          onChange={(e) => setRecipients({ url: e.target.value })}
        />
      </div>
    )
  }
  const isEmail = action.channel === 'email'
  const explicitList = (isEmail ? (action.recipients as any).emails : (action.recipients as any).phones) as string[] | undefined
  const explicitKey = isEmail ? 'emails' : 'phones'
  const placeholder = isEmail ? 'um.email@empresa.com, outro@empresa.com' : '+5511999998888, +5511988887777'
  const rolesCurrent = ((action.recipients as any).roles as RoleSelector[] | undefined) ?? []
  const toggleRole = (r: RoleSelector) => {
    const next = rolesCurrent.includes(r) ? rolesCurrent.filter((x) => x !== r) : [...rolesCurrent, r]
    setRecipients({ roles: next.length === 0 ? undefined : next })
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.sm }}>
      <div>
        <label style={labelStyle}>Por papel (todos da empresa com esse papel)</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(Object.keys(ROLE_LABELS) as RoleSelector[]).map((r) => {
            const on = rolesCurrent.includes(r)
            return (
              <button
                key={r} type='button' onClick={() => toggleRole(r)}
                style={{
                  fontSize: 11, fontWeight: 600, padding: '4px 8px',
                  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
                  border: `1px solid ${on ? TOKENS.color.action : TOKENS.color.border}`,
                  background: on ? TOKENS.color.actionSoft : TOKENS.color.card,
                  color: on ? TOKENS.color.action : TOKENS.color.muted,
                }}
              >{ROLE_LABELS[r]}</button>
            )
          })}
        </div>
      </div>
      <div>
        <label style={labelStyle}>Adicionais (separados por vírgula)</label>
        <input
          type='text'
          style={inputStyle}
          placeholder={placeholder}
          value={(explicitList ?? []).join(', ')}
          onChange={(e) => {
            const list = e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
            setRecipients({ [explicitKey]: list.length === 0 ? undefined : list })
          }}
        />
      </div>
    </div>
  )
}

export const RuleEditor: FC<Props> = ({ initial, defaultScope, defaultCompanyId, onSave, onCancel, saving, premiumEnabled = false }) => {
  const [name, setName] = useState(initial?.name ?? '')
  const [description, setDescription] = useState(initial?.description ?? '')
  const [scope, setScope] = useState<'global' | 'company'>(initial?.scope ?? defaultScope)
  const [companyId, setCompanyId] = useState<number | null>(initial?.company_id ?? defaultCompanyId ?? null)
  const [enabled, setEnabled] = useState(initial?.enabled ?? true)
  const [triggers, setTriggers] = useState<NotificationRuleTriggerDTO[]>(
    initial?.triggers?.length
      ? initial.triggers.map((t) => ({
          trigger_type: t.trigger_type as TriggerType,
          params: t.params ?? {},
          repeat_strategy: t.repeat_strategy ?? 'none',
          repeat_interval_minutes: t.repeat_interval_minutes ?? null,
          escalation_steps_minutes: t.escalation_steps_minutes ?? null,
        }))
      : [emptyTriggerForType('alarm_active')],
  )
  const [actions, setActions] = useState<NotificationRuleActionDTO[]>(
    initial?.actions?.length
      ? initial.actions.map((a) => ({
          channel: a.channel as ActionChannel,
          recipients: a.recipients as any,
          template: a.template ?? null,
          params: a.params as any,
        }))
      : [emptyActionForChannel('email')],
  )
  const [discardOpen, setDiscardOpen] = useState(false)

  useEffect(() => {
    if (!initial) {
      setScope(defaultScope)
      if (defaultCompanyId) setCompanyId(defaultCompanyId)
    }
  }, [initial, defaultScope, defaultCompanyId])

  const valid = name.trim().length > 0
    && triggers.length > 0
    && actions.length > 0
    && (scope === 'global' || (scope === 'company' && companyId !== null))

  const submit = async () => {
    if (!valid) return
    await onSave({
      name: name.trim(),
      description: description?.trim() || null,
      scope,
      company_id: scope === 'company' ? companyId : null,
      enabled,
      triggers,
      actions,
    })
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      {/* Header info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: TOKENS.spacing.md }}>
        <div>
          <label style={labelStyle}>Nome</label>
          <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder='Ex: Notificar admins ao receber alarme' />
        </div>
        <div>
          <label style={labelStyle}>Escopo</label>
          <select
            style={inputStyle}
            value={scope}
            onChange={(e) => setScope(e.target.value as 'global' | 'company')}
            disabled={!!initial /* lock scope on edit; create lets you choose */}
          >
            <option value='company'>Empresa</option>
            <option value='global'>Global (admin)</option>
          </select>
        </div>
      </div>
      <div>
        <label style={labelStyle}>Descrição (opcional)</label>
        <input style={inputStyle} value={description ?? ''} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: TOKENS.color.text }}>
        <input type='checkbox' checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Regra ativa
      </label>

      {/* Triggers */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: TOKENS.spacing.sm }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text }}>Triggers</div>
          <div style={{ fontSize: 11, color: TOKENS.color.muted }}>Qualquer um que ocorrer dispara a regra (OR)</div>
        </div>
        {triggers.map((t, i) => (
          <div key={i} style={sectionCard}>
            <div style={{ display: 'flex', gap: TOKENS.spacing.sm, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Tipo</label>
                <select
                  style={inputStyle}
                  value={t.trigger_type}
                  onChange={(e) => {
                    const next = e.target.value as TriggerType
                    setTriggers((arr) => arr.map((x, j) => j === i ? emptyTriggerForType(next) : x))
                  }}
                >
                  {(Object.keys(TRIGGER_LABELS) as TriggerType[]).map((tt) => (
                    <option key={tt} value={tt}>{TRIGGER_LABELS[tt]}</option>
                  ))}
                </select>
              </div>
              <button type='button' onClick={() => setTriggers((arr) => arr.filter((_, j) => j !== i))}
                style={{ background: 'transparent', border: 'none', color: TOKENS.color.severity.high.text, cursor: 'pointer', padding: 6 }}
                title='Remover trigger' disabled={triggers.length <= 1}
              ><Trash2 size={14} /></button>
            </div>
            <TriggerParamsEditor trigger={t} onChange={(next) => setTriggers((arr) => arr.map((x, j) => j === i ? next : x))} />
            <RepeatStrategyEditor
              trigger={t}
              premiumEnabled={premiumEnabled}
              onChange={(next) => setTriggers((arr) => arr.map((x, j) => j === i ? next : x))}
            />
          </div>
        ))}
        <button type='button' onClick={() => setTriggers((arr) => [...arr, emptyTriggerForType('alarm_active')])}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: TOKENS.color.card, border: `1px solid ${TOKENS.color.border}`,
            color: TOKENS.color.action, fontSize: 12, fontWeight: 600,
            padding: '6px 10px', borderRadius: TOKENS.radius.chip, cursor: 'pointer',
          }}><Plus size={14} /> Adicionar trigger</button>
      </div>

      {/* Actions */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: TOKENS.spacing.sm }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text }}>Ações</div>
          <div style={{ fontSize: 11, color: TOKENS.color.muted }}>Todas executam quando a regra dispara (AND)</div>
        </div>
        {actions.map((a, i) => (
          <div key={i} style={sectionCard}>
            <div style={{ display: 'flex', gap: TOKENS.spacing.sm, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Canal</label>
                <select
                  style={inputStyle}
                  value={a.channel}
                  onChange={(e) => setActions((arr) => arr.map((x, j) => j === i ? emptyActionForChannel(e.target.value as ActionChannel) : x))}
                >
                  {(Object.keys(CHANNEL_LABELS) as ActionChannel[]).map((c) => (
                    <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>
                  ))}
                </select>
              </div>
              <span style={{ color: TOKENS.color.muted }}>{channelIcon[a.channel]}</span>
              <button type='button' onClick={() => setActions((arr) => arr.filter((_, j) => j !== i))}
                style={{ background: 'transparent', border: 'none', color: TOKENS.color.severity.high.text, cursor: 'pointer', padding: 6 }}
                title='Remover ação' disabled={actions.length <= 1}
              ><Trash2 size={14} /></button>
            </div>
            <div style={{ marginTop: TOKENS.spacing.sm }}>
              <RecipientsEditor action={a} onChange={(next) => setActions((arr) => arr.map((x, j) => j === i ? next : x))} />
            </div>
            {a.channel === 'email' && (
              <div style={{ marginTop: TOKENS.spacing.sm }}>
                <label style={labelStyle}>Assunto (opcional, suporta placeholders)</label>
                <input
                  style={inputStyle}
                  placeholder='Ex: [CMB] Alarme em {cmb_number} ({alarm_type})'
                  value={(a.params as any)?.subject ?? ''}
                  onChange={(e) => setActions((arr) => arr.map((x, j) => j === i ? { ...x, params: { ...(x.params ?? {}), subject: e.target.value } } : x))}
                />
              </div>
            )}
            <div style={{ marginTop: TOKENS.spacing.sm }}>
              <label style={labelStyle}>Mensagem (opcional, suporta placeholders)</label>
              <textarea
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                placeholder='Ex: Caixa CMB {cmb_number} acionou alarme de {alarm_type}.'
                value={a.template ?? ''}
                onChange={(e) => setActions((arr) => arr.map((x, j) => j === i ? { ...x, template: e.target.value || null } : x))}
              />
              <div style={{ fontSize: 10, color: TOKENS.color.muted, marginTop: 4 }}>
                Placeholders: {'{cmb_number} {alarm_type} {company_name} {project_name} {fired_at} {bluetooth_id} {trigger_type}'}
              </div>
            </div>
          </div>
        ))}
        <button type='button' onClick={() => setActions((arr) => [...arr, emptyActionForChannel('email')])}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: TOKENS.color.card, border: `1px solid ${TOKENS.color.border}`,
            color: TOKENS.color.action, fontSize: 12, fontWeight: 600,
            padding: '6px 10px', borderRadius: TOKENS.radius.chip, cursor: 'pointer',
          }}><Plus size={14} /> Adicionar ação</button>
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: TOKENS.spacing.sm, marginTop: TOKENS.spacing.md, paddingTop: TOKENS.spacing.md, borderTop: `1px solid ${TOKENS.color.border}` }}>
        <button type='button' onClick={() => setDiscardOpen(true)}
          style={{ padding: '8px 14px', fontSize: 13, fontWeight: 600,
            border: `1px solid ${TOKENS.color.border}`, background: TOKENS.color.card, color: TOKENS.color.text,
            borderRadius: TOKENS.radius.chip, cursor: 'pointer' }}
        >Cancelar</button>
        <button type='button' onClick={submit} disabled={!valid || saving}
          style={{ padding: '8px 14px', fontSize: 13, fontWeight: 700,
            border: 'none', background: (!valid || saving) ? TOKENS.color.border : TOKENS.color.action,
            color: (!valid || saving) ? TOKENS.color.muted : '#fff',
            borderRadius: TOKENS.radius.chip, cursor: (!valid || saving) ? 'not-allowed' : 'pointer' }}
        >{saving ? 'Salvando…' : 'Salvar regra'}</button>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title='Descartar alterações?'
        description='Mudanças não salvas serão perdidas.'
        confirmLabel='Descartar'
        tone='danger'
        onConfirm={() => { setDiscardOpen(false); onCancel() }}
        onCancel={() => setDiscardOpen(false)}
      />
    </div>
  )
}
